import React, { useState, useRef, useEffect } from 'react';
import './DropdownMenu.css';

/**
 * DropdownMenu - Replaces Chakra UI MenuRoot/MenuTrigger/MenuContent/MenuItem/MenuSeparator
 *
 * Usage:
 *   <DropdownMenu trigger={<button>Open</button>}>
 *     <DropdownMenuItem onClick={handleClick}>Item 1</DropdownMenuItem>
 *     <DropdownMenuSeparator />
 *     <DropdownMenuItem onClick={handleClick}>Item 2</DropdownMenuItem>
 *   </DropdownMenu>
 */

export function DropdownMenu({ trigger, children, align = 'right' }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    function handleEscape(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  return (
    <div className="dd-dropdown" ref={menuRef}>
      <div className="dd-dropdown-trigger" onClick={() => setOpen(prev => !prev)}>
        {trigger}
      </div>
      {open && (
        <div className={`dd-dropdown-content dd-dropdown-${align}`} role="menu">
          {React.Children.map(children, child => {
            if (!React.isValidElement(child)) return child;
            if (child.type === DropdownMenuItem) {
              return React.cloneElement(child, {
                onClick: (...args) => {
                  setOpen(false);
                  child.props.onClick?.(...args);
                }
              });
            }
            return child;
          })}
        </div>
      )}
    </div>
  );
}

export function DropdownMenuItem({ onClick, children, className = '' }) {
  return (
    <button
      className={`dd-dropdown-item ${className}`}
      role="menuitem"
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export function DropdownMenuSeparator() {
  return <hr className="dd-dropdown-separator" />;
}
